/**
 * FO-410 (batch-54) - canonieke placeholder-key: ÉÉN normalisatie voor de hele
 * document-generatieketen.
 *
 * Probleem (FO-410 G1): een sjabloon draagt vaak twee schrijfwijzen van hetzelfde
 * gegeven (`{{OPDRACHTGEVER_NAAM}}` én `{{Opdrachtgever naam}}`). De RENDER-kant is
 * daar al ongevoelig voor (de docx-resolver matcht case-/whitespace-ongevoelig),
 * maar de scanner, de wizard en de config-editor tonen elke ruwe variant als een
 * eigen invulveld - twee keer invullen, en de varianten kunnen elkaar tegenspreken.
 *
 * Deze module levert die ene normalisatie plus de groepeer-/samenvoeg-helpers die
 * scanner + wizard + config-editor delen. Bewust in `packages/types` (pure, geen
 * runtime-deps) zodat zowel `apps/api` als de Next-apps hem kunnen importeren:
 *  - `apps/api/src/lib/docx-placeholder-resolver.ts` gebruikt hem als alfanumerieke
 *    alias (render);
 *  - `apps/api/src/lib/document-template-scanner.ts` groepeert er de scan mee;
 *  - `document-placeholders.ts` (registry) en de config-editor lookuppen ermee.
 *
 * Harde grens (FO-410 §6.2 / A2): canonicalisatie normaliseert ALLEEN schrijfwijze
 * (case, underscores, koppeltekens, spaties, slashes). Inhoudelijk verschillende
 * keys blijven aparte velden - `datum` en `datum_ondertekening` mogen NOOIT
 * samenvallen (geen synoniem-dedupe).
 */

/**
 * De canonieke vorm van een placeholder-key: trim + lowercase + alle
 * niet-alfanumerieke tekens eruit.
 *
 * `OPDRACHTGEVER_NAAM`, `Opdrachtgever naam`, `opdrachtgever-naam` en
 * `Opdrachtgever/Naam` → allemaal `opdrachtgevernaam`.
 * `datum` → `datum`, `datum_ondertekening` → `datumondertekening` (blijven apart).
 */
export function canonicalPlaceholderKey(key: string): string {
  return key.trim().toLowerCase().replace(/[^a-z0-9]+/g, '')
}

/**
 * Is deze key een docxtemplater SECTIE-sleutel (`{{#x}}`/`{{/x}}`/`{{^x}}`)?
 * Die zijn render-controle, geen invulveld, en doen niet mee in de dedupe.
 */
export function isPlaceholderSectieSleutel(key: string): boolean {
  const t = key.trim()
  return t.startsWith('#') || t.startsWith('/') || t.startsWith('^')
}

/**
 * "Netheids"-score van een schrijfwijze-variant: hoe geschikt is deze variant als
 * zichtbaar label / primaire key? Hoger = netter.
 *
 * De rangorde volgt wat een gebruiker in het invulscherm wil zien:
 *  +2  bevat woordscheiding (spatie/underscore/koppelteken) - leesbaar
 *  +2  niet volledig HOOFDLETTERS - "Opdrachtgever naam" leest beter dan
 *      "OPDRACHTGEVER_NAAM"
 *  +1  begint met een hoofdletter (oogt als een label)
 * Bij gelijke score wint de EERST gescande variant (stabiele, voorspelbare keuze).
 */
export function placeholderVariantNetheid(variant: string): number {
  const v = variant.trim()
  if (!v) return -1
  let score = 0
  if (/[ _-]/.test(v)) score += 2
  const letters = v.replace(/[^a-zA-Z]/g, '')
  if (letters && v !== v.toUpperCase()) score += 2
  if (/^[A-Z]/.test(v)) score += 1
  return score
}

/** Eén canonieke groep: alle schrijfwijze-varianten van hetzelfde gegeven. */
export interface PlaceholderVariantGroep {
  /** De canonieke key (`opdrachtgevernaam`) - de sleutel waarop gededupliceerd is. */
  canoniekeKey: string
  /**
   * De variant die het formulier gebruikt als veld-key én labelbron: de netste
   * schrijfwijze (zie `placeholderVariantNetheid`).
   */
  primaireKey: string
  /** Alle voorkomende schrijfwijzen, in scan-volgorde (incl. `primaireKey`). */
  varianten: string[]
  /** Aantal verschillende schrijfwijzen ("komt {n}x voor in het sjabloon"). */
  aantal: number
}

/**
 * Groepeer ruwe placeholder-keys op hun canonieke vorm. Sectie-sleutels
 * (`#x`/`/x`/`^x`) worden overgeslagen; lege keys ook. De groepen komen terug in
 * volgorde van eerste voorkomen, zodat de veldvolgorde van het sjabloon blijft.
 */
export function groepeerPlaceholderVarianten(
  keys: readonly string[],
): PlaceholderVariantGroep[] {
  const groepen = new Map<string, { varianten: string[] }>()
  for (const raw of keys) {
    const key = raw.trim()
    if (!key || isPlaceholderSectieSleutel(key)) continue
    const canoniek = canonicalPlaceholderKey(key)
    if (!canoniek) continue
    const groep = groepen.get(canoniek)
    if (!groep) {
      groepen.set(canoniek, { varianten: [key] })
      continue
    }
    if (!groep.varianten.includes(key)) groep.varianten.push(key)
  }
  return Array.from(groepen.entries()).map(([canoniekeKey, { varianten }]) => ({
    canoniekeKey,
    primaireKey: kiesPrimaireVariant(varianten),
    varianten,
    aantal: varianten.length,
  }))
}

/**
 * De variant die als veld-key/label gebruikt wordt: hoogste netheids-score, bij
 * gelijkspel de eerst gescande. Lege lijst → lege string (caller filtert die weg).
 */
export function kiesPrimaireVariant(varianten: readonly string[]): string {
  let beste = ''
  let besteScore = -Infinity
  for (const v of varianten) {
    const score = placeholderVariantNetheid(v)
    if (score > besteScore) {
      beste = v
      besteScore = score
    }
  }
  return beste
}

/**
 * FO-410 §A1 - vul bij het RENDEREN alle schrijfwijze-varianten met de ene
 * ingevulde waarde. De wizard levert nog maar één veld per canoniek gegeven; de
 * waarden-pool moet daarna élke variant dekken, anders rendert de niet-ingevulde
 * variant leeg (de docx-resolver vindt dan een expliciete lege string en zoekt
 * niet verder).
 *
 * Regels:
 *  - de niet-lege waarde binnen een canonieke groep wint en wordt op alle
 *    varianten gezet;
 *  - meerdere niet-lege, VERSCHILLENDE waarden binnen één groep (legacy-data):
 *    de eerste niet-lege wint, deterministisch op sleutelvolgorde van de input;
 *  - keys zonder groepsgenoten blijven ongewijzigd.
 *
 * Pure functie: geeft een nieuwe map terug, muteert de input niet.
 *
 * LET OP - dit is een fill-empty-only helper en mag NOOIT de enige propagatie-
 * stap zijn op een pool die ook auto-fill-waarden bevat. Hij kent de auto-laag
 * niet en kan een BEWERKTE waarde dus niet van een STALE zusje onderscheiden;
 * een niet-lege variant blijft daarom staan. De groepsbeslissing ("deze ene
 * waarde geldt voor alle schrijfwijzen") hoort een laag hoger, in
 * `mergePlaceholderValues`. Sla die over en er staan weer twee verschillende
 * waarden voor hetzelfde gegeven in het document (FO-410 gebrek G1).
 */
export function vulPlaceholderVarianten(
  values: Record<string, string>,
): Record<string, string> {
  const perCanoniek = new Map<string, string>()
  for (const [key, waarde] of Object.entries(values)) {
    const canoniek = canonicalPlaceholderKey(key)
    if (!canoniek) continue
    const gevuld = (waarde ?? '').trim()
    if (!gevuld) continue
    if (!perCanoniek.has(canoniek)) perCanoniek.set(canoniek, waarde)
  }
  const out: Record<string, string> = {}
  for (const [key, waarde] of Object.entries(values)) {
    const canoniek = canonicalPlaceholderKey(key)
    const gevuldeVariant = canoniek ? perCanoniek.get(canoniek) : undefined
    out[key] = (waarde ?? '').trim() ? waarde : (gevuldeVariant ?? waarde)
  }
  return out
}

/**
 * Zoek de waarde van een canoniek gegeven in een waarden-map, ongeacht de
 * schrijfwijze waaronder hij daar staat. Gebruikt door de auto-fill-filter: een
 * sjabloon dat `{{Opdrachtgever naam}}` verwacht moet de auto-fill-waarde onder
 * `OPDRACHTGEVER_NAAM` gewoon krijgen.
 */
export function zoekCanoniekeWaarde(
  values: Record<string, string>,
  key: string,
): string | undefined {
  const direct = values[key]
  if (direct !== undefined && direct !== '') return direct
  const canoniek = canonicalPlaceholderKey(key)
  if (!canoniek) return undefined
  for (const [k, v] of Object.entries(values)) {
    if (v !== undefined && v !== '' && canonicalPlaceholderKey(k) === canoniek) return v
  }
  return undefined
}
